// Spawn-legality (placement-from-hand) module. Pure. No React, no
// Three.js. Owns the canonical "where can this card be SUMMONED?"
// computation. Distinct from movement.js, which answers "where can
// this on-board card STEP to?" Movement has a source cell+region,
// summoning does not.
//
// ── Sorcery rules baked in (codex "Play", "Summon", "Site",
//     "Voidwalk", "Rubble") ──
//
//   • A Minion or Avatar can only be summoned onto a cell whose
//     surface holds a Site controlled by the casting player.
//   • Rubble IS a site by type, but per the codex it has no affinity
//     and provides no mana. And the placement rule we surface here
//     follows the user's call: Rubble doesn't count as a real site
//     for summoning purposes (you can't summon onto your own Rubble).
//   • Voidwalk extends (does NOT replace) the controlled-site rule.
//     Per codex "Voidwalk": "A minion with Voidwalk can be summoned
//     to void locations and survive in the void." Combined with codex
//     "Void": "A square without a site is a void." So a Voidwalker
//     can be summoned to (a) any cell with your own non-Rubble site,
//     OR (b) any cell with NO site at all (true void). It cannot be
//     summoned onto an opponent's site, and it cannot be summoned
//     onto Rubble (Rubble is still a site, so the cell isn't void).
//
// Avatars are treated the same as Minions here (per the user's call:
// the "Imposter" avatar may include other avatars in its deck. A
// custom rule for later; baseline behaviour matches Minions).
//
// ── Return shape ──
//   spawnableCellsForMinion(...)
//     → Set<'col,row'>. Every cell where this card can legally be
//       summoned by the given controller.

import { hasVoidwalk } from './movement';
import {
  hasBurrowing, hasSubmerge,
  cellHasUnderground, cellHasUnderwater,
} from './burrowSubmerge';
import { regionsAtCell } from './regions';

const RUBBLE_ID = 'sorcery-rubble';

function cellHasControlledNonRubbleSite(meshes, cellAt, col, row, controllerRotated) {
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    if (card.cardId === RUBBLE_ID) continue;
    if (!!card.rotated !== !!controllerRotated) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    if (cell.col === col && cell.row === row) return true;
  }
  return false;
}

// A cell is "void" iff no Site of any kind (including Rubble or
// opponent sites) currently occupies it. Used for the Voidwalk
// summon path.
function cellIsVoid(meshes, cellAt, col, row) {
  for (const [, mesh] of meshes) {
    const card = mesh?.userData?.cardInstance;
    if (!card?.isSite) continue;
    const cell = cellAt(mesh.position.x, mesh.position.z);
    if (!cell) continue;
    if (cell.col === col && cell.row === row) return false;
  }
  return true;
}

/**
 * Cells where `card` (a Minion or Avatar in the casting player's
 * hand) can be summoned. `controllerRotated` is the boolean used
 * across the codebase to identify which side a card belongs to.
 *
 * v1.111. `extraCells` is an optional Set of `'col,row'` keys
 * that the caller wants unioned into the result regardless of the
 * standard adjacency-to-controlled-site rule. This is where the
 * Harbinger's marked squares plug in: when the marks still have an
 * unused once-per-turn charge AND the candidate is a Minion, the
 * controller is allowed to summon onto those cells with no other
 * placement preconditions.
 */
export function spawnableCellsForMinion(
  card, controllerRotated, totalCols, totalRows, meshes, cellAt,
  extraCells = null,
) {
  const out = new Set();
  if (!card) return out;
  const voidwalker = hasVoidwalk(card);
  for (let c = 0; c < totalCols; c++) {
    for (let r = 0; r < totalRows; r++) {
      if (cellHasControlledNonRubbleSite(meshes, cellAt, c, r, controllerRotated)) {
        out.add(`${c},${r}`);
        continue;
      }
      if (voidwalker && cellIsVoid(meshes, cellAt, c, r)) {
        out.add(`${c},${r}`);
      }
    }
  }
  if (extraCells && typeof extraCells.forEach === 'function') {
    extraCells.forEach((key) => out.add(key));
  }
  return out;
}

/**
 * Cells where `card` can be SUMMONED directly into the subsurface
 * (used when the player is already in the subsurface view). The
 * candidate set is the controlled-non-Rubble cell set from
 * spawnableCellsForMinion, further filtered to cells whose subsurface
 * region matches one of the card's keywords:
 *   • Burrowing → cell must expose 'underground'
 *   • Submerge  → cell must expose 'underwater'
 * Cards with neither keyword return an empty set (can't be summoned
 * subsurface at all). Voidwalk does NOT apply. True void has no
 * subsurface region to land in.
 *
 * v1.111. `extraCells` carries the same Harbinger marks plumbed
 * into spawnableCellsForMinion. Each extra cell still has to clear
 * the subsurface-region check (the codex placement-extension lets
 * you summon there, but a Burrowing minion still needs an under-
 * ground region to enter, and a Submerge minion needs water).
 */
export function spawnableCellsForSubsurfacePlay(
  card, controllerRotated, totalCols, totalRows, meshes, cellAt, getFullCard,
  extraCells = null,
) {
  const out = new Set();
  if (!card) return out;
  const burrows = hasBurrowing(card);
  const submerges = hasSubmerge(card);
  if (!burrows && !submerges) return out;
  const hasExtra = (col, row) => {
    if (!extraCells || typeof extraCells.has !== 'function') return false;
    return extraCells.has(`${col},${row}`);
  };
  for (let c = 0; c < totalCols; c++) {
    for (let r = 0; r < totalRows; r++) {
      if (!cellHasControlledNonRubbleSite(meshes, cellAt, c, r, controllerRotated)
          && !hasExtra(c, r)) continue;
      const ug = burrows && cellHasUnderground(meshes, cellAt, c, r, getFullCard);
      const uw = submerges && cellHasUnderwater(meshes, cellAt, c, r, getFullCard);
      if (ug || uw) out.add(`${c},${r}`);
    }
  }
  return out;
}

/**
 * Pick the subsurface region a card should enter when played onto
 * (col, row) in subsurface view. Returns:
 *   'underground'. Burrowing card and cell exposes underground
 *   'underwater'. Submerge card and cell exposes underwater
 *   null. Card lacks the keyword for any region present
 *                   at this cell (caller should hard-block the play).
 *
 * Ambiguity rule: when a card has BOTH Burrowing and Submerge and the
 * cell exposes both subsurface regions, the codex doesn't pick for us
 * We prefer 'underground'. The radial menu's surface/burrow/
 * submerge ring lets the player swap regions after landing.
 */
export function subsurfaceRegionForPlay(
  card, meshes, cellAt, col, row, getFullCard,
) {
  if (!card) return null;
  const burrows = hasBurrowing(card);
  const submerges = hasSubmerge(card);
  if (!burrows && !submerges) return null;
  const regions = regionsAtCell(meshes, cellAt, col, row, getFullCard);
  if (burrows && regions.has('underground')) return 'underground';
  if (submerges && regions.has('underwater')) return 'underwater';
  return null;
}
